import { useEffect, useState } from 'react';
import { customerService } from '@services/api/customerService';
import { getAccountStatusColor, formatCurrency } from '@/types/customer';
import type { Customer, Account, Card, Device } from '@/types/customer';
import '../CustomerTabs.css';

interface OverviewTabProps {
  customer: Customer;
}

export const OverviewTab = ({ customer }: OverviewTabProps) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, [customer.id]);

  const loadOverviewData = async () => {
    const [accountsRes, cardsRes, devicesRes] = await Promise.all([
      customerService.getCustomerAccounts(customer.id),
      customerService.getCustomerCards(customer.id),
      customerService.getCustomerDevices(customer.id),
    ]);

    if (accountsRes.success) setAccounts(accountsRes.data || []);
    if (cardsRes.success) setCards(cardsRes.data || []);
    if (devicesRes.success) setDevices(devicesRes.data || []);
    
    setLoading(false);
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + Number(acc.balance), 0);

  return (
    <div className="tab-content-wrapper">
      {/* AI Summary Placeholder */}
      <div className="ai-summary-card">
        <h3>🤖 AI Customer Summary</h3>
        <p className="text-secondary">
          AI-generated customer insights will appear here in Phase 3
        </p>
      </div>

      <div className="tab-grid-2">
        {/* Basic Information */}
        <div className="info-card">
          <h3 className="info-card-title">📋 Basic Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Customer ID:</span>
              <span className="info-value">{customer.id.slice(0, 13)}...</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{customer.user_email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">{customer.phone_number}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Emirates ID:</span>
              <span className="info-value">{customer.emirates_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Date of Birth:</span>
              <span className="info-value">{customer.date_of_birth}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Nationality:</span>
              <span className="info-value">{customer.nationality}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Gender:</span>
              <span className="info-value">{customer.gender}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className={`status-badge status-${getAccountStatusColor(customer.account_status)}`}>
                {customer.account_status}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="info-card">
          <h3 className="info-card-title">🎯 Quick Actions</h3>
          <div className="quick-actions">
            <button className="btn btn-primary btn-small">Create Case</button>
            <button className="btn btn-secondary btn-small">Send Message</button>
            <button className="btn btn-secondary btn-small">View Full History</button>
            <button className="btn btn-ghost btn-small">Generate Report</button>
          </div>
        </div>
      </div>

      {/* Account Summary */}
      <div className="info-card">
        <h3 className="info-card-title">💰 Account Summary</h3>
        {loading ? (
          <p>Loading...</p>
        ) : accounts.length === 0 ? (
          <p className="text-secondary">No accounts</p>
        ) : (
          <>
            {accounts.map((account) => (
              <div key={account.id} className="account-summary-item">
                <div className="account-summary-info">
                  <span className="account-summary-name">
                    {account.account_type} Account (****{account.account_number.slice(-4)})
                  </span>
                  <span className="account-summary-balance">
                    {formatCurrency(Number(account.balance), account.currency)}
                  </span>
                </div>
                <button className="btn btn-ghost btn-small">View Details →</button>
              </div>
            ))}
            <div className="account-summary-total">
              <span>Total Balance:</span>
              <span className="total-balance">{formatCurrency(totalBalance)}</span>
            </div>
          </>
        )}
      </div>

      {/* Card Summary */}
      <div className="info-card">
        <h3 className="info-card-title">💳 Cards</h3>
        {loading ? (
          <p>Loading...</p>
        ) : cards.length === 0 ? (
          <p className="text-secondary">No cards</p>
        ) : (
          <>
            {cards.map((card) => (
              <div key={card.id} className="card-summary-item">
                <div className="card-summary-info">
                  <span className="card-summary-name">
                    {card.card_type} Card (****{card.last_four_digits})
                  </span>
                  <span className={`status-badge status-${card.card_status === 'ACTIVE' ? 'green' : 'red'}`}>
                    {card.card_status}
                  </span>
                </div>
                <div className="card-summary-limits">
                  Daily Limit: {formatCurrency(Number(card.daily_transaction_limit))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Devices Panel */}
      <div className="info-card">
        <h3 className="info-card-title">📱 Devices</h3>
        {loading ? (
          <p>Loading...</p>
        ) : devices.length === 0 ? (
          <p className="text-secondary">No devices registered</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Type</th>
                  <th>OS</th>
                  <th>Status</th>
                  <th>Last Used</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id}>
                    <td>{device.device_name || device.model_name || 'Unknown'}</td>
                    <td className="text-capitalize">{device.device_type || 'N/A'}</td>
                    <td>{device.os_name} {device.os_version}</td>
                    <td>
                      <span className={`status-badge status-${device.status === 'active' ? 'green' : 'gray'}`}>
                        {device.status}
                      </span>
                    </td>
                    <td className="text-small">
                      {device.last_used_at 
                        ? new Date(device.last_used_at).toLocaleString() 
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

